import { matchBulkReference, parseTSV, type BulkReferenceOption } from '../bulk/public';
import type { StaffingPlanInput, StaffingWorker } from '../staffing/public';
import type { OrganizationNode } from './model';
import { plannedOrganizationId, previewOrganizationPlan } from './organizationPlan';

export type StudioBulkKind = 'organizations' | 'workers';
export type StudioBulkField = 'organization' | 'parent' | 'manager' | 'worker' | 'jobProfile' | 'location';
export type StudioBulkColumn = { key: string; label: string; required?: boolean; example: string; reference?: StudioBulkField };
export const studioBulkColumns: Record<StudioBulkKind, StudioBulkColumn[]> = {
  organizations: [
    { key: 'operation', label: '작업', required: true, example: '신설' },
    { key: 'organization', label: '대상 조직', example: '', reference: 'organization' },
    { key: 'name', label: '조직 이름', example: '플랫폼팀' },
    { key: 'parent', label: '상위 조직', example: '개발본부', reference: 'parent' },
    { key: 'manager', label: '리더', example: '', reference: 'manager' },
  ],
  workers: [
    { key: 'worker', label: '구성원', required: true, example: '루카(김하늘)', reference: 'worker' },
    { key: 'organization', label: '이동 조직', example: '플랫폼팀', reference: 'organization' },
    { key: 'jobProfile', label: '직무', example: '', reference: 'jobProfile' },
    { key: 'location', label: '근무지', example: '', reference: 'location' },
    { key: 'businessTitle', label: '직함', example: '', },
  ],
};
export type StudioBulkRow = { rowNumber: number; values: Record<string, string> };
export type StudioBulkSelections = Record<string, string>;
export type StudioBulkContext = {
  plan: StaffingPlanInput;
  organizations: readonly OrganizationNode[];
  workers: readonly StaffingWorker[];
  jobProfiles?: readonly { id: string; descriptor: string }[];
  locations?: readonly { id: string; descriptor: string }[];
};
export type StudioBulkMatch = { field: StudioBulkField; input: string; option?: BulkReferenceOption; options: BulkReferenceOption[]; error?: string };
export type StudioBulkReviewRow = StudioBulkRow & { errors: string[]; matches: StudioBulkMatch[]; changes: string[] };
export type StudioBulkReview = { rows: StudioBulkReviewRow[]; error?: string; nextPlan?: StaffingPlanInput };
export const studioBulkSelectionKey = (rowNumber: number, field: StudioBulkField) => `${rowNumber}:${field}`;
export const studioBulkClearValue = '[해제]';

export function parseStudioBulkInput(kind: StudioBulkKind, input: string): StudioBulkRow[] {
  const parsed = parseTSV(input, 500);
  const columns = studioBulkColumns[kind];
  const mapped = parsed.headers.map((header) => columns.find((column) => column.label === header || column.key === header)?.key);
  const unknown = parsed.headers.filter((_, index) => !mapped[index]);
  if (unknown.length) throw new Error(`양식에 없는 열이 있습니다: ${unknown.join(', ')}`);
  if (new Set(mapped).size !== mapped.length) throw new Error('같은 항목에 해당하는 열이 중복되었습니다.');
  const missing = columns.filter((column) => column.required && !mapped.includes(column.key));
  if (missing.length) throw new Error(`필수 열이 없습니다: ${missing.map((column) => column.label).join(', ')}`);
  return parsed.rows.map((row, index) => ({ rowNumber: index + 2, values: Object.fromEntries(parsed.headers.map((header, headerIndex) => [mapped[headerIndex]!, row[header]])) }));
}

export function studioBulkWorkerOptions(workers: readonly StaffingWorker[]): BulkReferenceOption[] {
  return workers.map((worker) => ({
    id: worker.id, label: worker.name, description: [worker.organization, worker.title, worker.workerNumber].filter(Boolean).join(' · '),
    aliases: [worker.workerNumber, ...(worker.name.match(/^(.+?)\s*\((.+)\)$/)?.slice(1).map((value) => value.trim()) ?? [])],
  }));
}

export function studioBulkOrganizationOptions(nodes: readonly OrganizationNode[]): BulkReferenceOption[] {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const path = (node: OrganizationNode) => {
    const names = [node.name];
    const seen = new Set([node.id]);
    let parent = node.parentId ? byId.get(node.parentId) : undefined;
    while (parent && !seen.has(parent.id)) {
      seen.add(parent.id); names.unshift(parent.name); parent = parent.parentId ? byId.get(parent.parentId) : undefined;
    }
    return names.join(' / ');
  };
  return nodes.map((node) => ({ id: node.id, label: node.name, description: path(node), aliases: [path(node)], searchText: path(node) }));
}

const reference = (id: string) => id.startsWith('plan:') ? { planItemKey: id.slice(5) } : { id };
const organizationId = (value?: { id?: string; planItemKey?: string }) => value?.planItemKey ? plannedOrganizationId(value.planItemKey) : value?.id;
const operation = (value: string): 'CREATE' | 'CHANGE' | 'END' | undefined => {
  if (value === '신설' || value === 'CREATE') return 'CREATE';
  if (value === '변경' || value === 'CHANGE') return 'CHANGE';
  if (value === '종료' || value === 'END') return 'END';
  return undefined;
};

/** 붙여넣기 결과를 현재 발령안에 추가할 수 있는지 판정합니다. 오류가 있으면 원본 초안을 반환하거나 변경하지 않습니다. */
export function reviewStudioBulkInput(
  kind: StudioBulkKind, source: readonly StudioBulkRow[], context: StudioBulkContext, batchKey: string, selections: StudioBulkSelections = {},
): StudioBulkReview {
  const rows: StudioBulkReviewRow[] = source.map((row) => ({ ...row, errors: [], matches: [], changes: [] }));
  if (!rows.length) return { rows, error: '한 개 이상의 변경을 입력해 주세요.' };
  if (rows.length + context.plan.organizationChanges.length + context.plan.workerChanges.length > 500) return { rows, error: '발령안은 기존 변경을 포함해 최대 500개 항목까지 작성할 수 있습니다.' };
  const keyFor = (row: StudioBulkRow) => `bulk:${batchKey}:${row.rowNumber}`;
  const ending = new Set(context.plan.organizationChanges.filter((row) => row.operation === 'END').map((row) => row.organizationId));
  const existingNodes = previewOrganizationPlan(context.organizations, context.plan.organizationChanges);
  const creates = kind === 'organizations' ? rows.filter((row) => operation(row.values.operation) === 'CREATE' && row.values.name) : [];
  const pendingNodes: OrganizationNode[] = creates.map((row) => ({ id: plannedOrganizationId(keyFor(row)), name: row.values.name, manager: '', count: 0, location: '', subtitle: '신설 예정' }));
  const allNodes = [...existingNodes, ...pendingNodes];
  // 같은 입력에서 먼저 신설한 조직도 상위 조직 이름·경로로 연결할 수 있습니다.
  for (let pass = 0; pass <= creates.length; pass += 1) {
    const options = studioBulkOrganizationOptions(allNodes);
    let changed = false;
    for (const row of creates) {
      const selected = selections[studioBulkSelectionKey(row.rowNumber, 'parent')];
      const matched = selected ? options.filter((option) => option.id === selected) : matchBulkReference(row.values.parent ?? '', options);
      const node = pendingNodes.find((item) => item.id === plannedOrganizationId(keyFor(row)))!;
      if (matched.length === 1 && node.parentId !== matched[0].id) { node.parentId = matched[0].id; changed = true; }
    }
    if (!changed) break;
  }
  const organizationOptions = studioBulkOrganizationOptions(allNodes);
  const workerOptions = studioBulkWorkerOptions(context.workers);
  const optionsFor = (field: StudioBulkField): BulkReferenceOption[] => field === 'manager' || field === 'worker' ? workerOptions
    : field === 'jobProfile' ? (context.jobProfiles ?? []).map((item) => ({ id: item.id, label: item.descriptor }))
      : field === 'location' ? (context.locations ?? []).map((item) => ({ id: item.id, label: item.descriptor })) : organizationOptions;
  const resolve = (row: StudioBulkReviewRow, field: StudioBulkField, label: string, required = false) => {
    const input = row.values[field] ?? '';
    const selectionKey = studioBulkSelectionKey(row.rowNumber, field);
    const selected = selections[selectionKey];
    if (!input && !selected && !required) return undefined;
    const options = optionsFor(field);
    const matches = Object.hasOwn(selections, selectionKey) ? options.filter((option) => option.id === selected) : input ? matchBulkReference(input, options) : [];
    const option = matches.length === 1 ? matches[0] : undefined;
    const error = option ? undefined : `${label}: ${matches.length > 1 ? '같은 이름이 여러 개 있습니다.' : '연결할 대상을 찾지 못했습니다.'} 검색해서 선택해 주세요.`;
    row.matches.push({ field, input, option, options, error });
    if (error) row.errors.push(error);
    return option;
  };
  const next: StaffingPlanInput = { ...context.plan, organizationChanges: [...context.plan.organizationChanges], workerChanges: [...context.plan.workerChanges] };
  const touchedOrganizations = new Set<string>();
  const touchedWorkers = new Set<string>();
  for (const row of rows) {
    const key = keyFor(row);
    if ([...context.plan.organizationChanges, ...context.plan.workerChanges].some((item) => item.key === key)) row.errors.push('이미 추가한 입력입니다. 새 입력으로 검증해 주세요.');
    if (kind === 'organizations') {
      const action = operation(row.values.operation);
      if (!action) { row.errors.push('작업은 신설, 변경, 종료 중 하나를 입력해 주세요.'); continue; }
      const target = action !== 'CREATE' ? resolve(row, 'organization', '대상 조직', true) : undefined;
      if (action === 'CREATE' && row.values.organization) row.errors.push('신설은 대상 조직을 비워 두고 조직 이름을 입력해 주세요.');
      if (action === 'CREATE' && !row.values.name) row.errors.push('신설할 조직 이름을 입력해 주세요.');
      if (action === 'END' && (row.values.name || row.values.parent || row.values.manager)) row.errors.push('조직 종료는 대상 조직만 입력해 주세요.');
      if (target?.id.startsWith('plan:')) row.errors.push('신설 예정 조직은 조직도에서 수정해 주세요.');
      if (target && (context.plan.organizationChanges.some((item) => item.organizationId === target.id) || touchedOrganizations.has(target.id))) row.errors.push('이미 변경이 있는 조직입니다. 조직도에서 기존 변경을 수정해 주세요.');
      if (target) touchedOrganizations.add(target.id);
      const parent = action !== 'END' ? resolve(row, 'parent', '상위 조직', action === 'CREATE' && context.organizations.length > 0) : undefined;
      const clearManager = row.values.manager === studioBulkClearValue;
      const manager = action !== 'END' && !clearManager ? resolve(row, 'manager', '리더') : undefined;
      if (parent && ending.has(parent.id)) row.errors.push('종료 예정 조직은 상위 조직으로 선택할 수 없습니다.');
      const original = context.organizations.find((node) => node.id === target?.id);
      if (original && !original.parentId && (parent || action === 'END')) row.errors.push('최상위 조직은 이동하거나 종료할 수 없습니다.');
      const name = row.values.name?.trim();
      const nameChange = name && name !== original?.name ? name : undefined;
      const parentChange = parent && parent.id !== original?.parentId ? reference(parent.id) : undefined;
      if (action === 'CHANGE' && !nameChange && !parentChange && !manager && !clearManager) row.errors.push('변경할 값을 하나 이상 입력해 주세요.');
      next.organizationChanges.push({ key, operation: action, ...(target ? { organizationId: target.id, organizationDescriptor: target.label } : {}), ...(nameChange ? { name: nameChange } : {}), ...(parentChange ? { parent: parentChange } : {}), ...(clearManager ? { managerId: null } : manager ? { managerId: manager.id } : {}) });
      row.changes = [action === 'CREATE' ? `조직 신설: ${name ?? ''}` : action === 'END' ? '조직 종료' : '', nameChange && action === 'CHANGE' ? `이름: ${nameChange}` : '', parentChange ? `상위 조직: ${parent?.description || parent?.label}` : '', clearManager ? '리더 해제' : manager ? `리더: ${manager.label}` : ''].filter(Boolean);
    } else {
      const worker = resolve(row, 'worker', '구성원', true);
      if (worker && (context.plan.workerChanges.some((item) => item.workerId === worker.id) || touchedWorkers.has(worker.id))) row.errors.push('이미 변경이 있는 구성원입니다. 조직도에서 기존 변경을 수정해 주세요.');
      if (worker) touchedWorkers.add(worker.id);
      const target = resolve(row, 'organization', '이동 조직');
      const jobProfile = resolve(row, 'jobProfile', '직무');
      const location = resolve(row, 'location', '근무지');
      if (target && ending.has(target.id)) row.errors.push('종료 예정 조직으로 이동할 수 없습니다.');
      const before = context.workers.find((item) => item.id === worker?.id);
      const title = row.values.businessTitle?.trim();
      const clearTitle = title === studioBulkClearValue;
      const titleChange = clearTitle ? '' : title && title !== before?.businessTitle ? title : undefined;
      const targetChange = target && target.id !== before?.organizationId ? reference(target.id) : undefined;
      const locationChange = location && location.id !== before?.locationId ? location.id : undefined;
      // 조회 모델에는 현재 직무 ID가 없으므로 같은 이름만으로 다른 직무를 제거하지 않습니다.
      const profileChange = jobProfile?.id;
      if (!targetChange && !profileChange && !locationChange && titleChange === undefined) row.errors.push('현재 값과 다른 변경을 하나 이상 입력해 주세요.');
      next.workerChanges.push({ key, operation: 'JOB_CHANGE', workerId: worker?.id ?? '', workerDescriptor: worker?.label, ...(targetChange ? { organization: targetChange } : {}), ...(profileChange ? { jobProfileId: profileChange } : {}), ...(locationChange ? { locationId: locationChange } : {}), ...(titleChange !== undefined ? { businessTitle: titleChange, businessTitleMode: clearTitle ? 'CLEAR' : 'CHANGE' } : {}) });
      row.changes = [targetChange ? `조직: ${before?.organization || '없음'} → ${target?.description || target?.label}` : '', profileChange ? `직무: ${before?.jobProfile || '없음'} → ${jobProfile?.label}` : '', locationChange ? `근무지: ${before?.location || '없음'} → ${location?.label}` : '', titleChange !== undefined ? `직함: ${before?.businessTitle || '없음'} → ${clearTitle ? '해제' : titleChange}` : ''].filter(Boolean);
    }
  }
  const finalNodes = previewOrganizationPlan(context.organizations, next.organizationChanges);
  const finalById = new Map(finalNodes.map((node) => [node.id, node]));
  const finalEnding = new Set(next.organizationChanges.filter((row) => row.operation === 'END').map((row) => row.organizationId));
  let graphError: string | undefined;
  for (const node of finalNodes) {
    if (finalEnding.has(node.id)) continue;
    if (node.parentId && finalEnding.has(node.parentId)) graphError = '종료할 조직에 하위 조직이 남아 있습니다. 하위 조직을 먼저 이동해 주세요.';
    const visited = new Set<string>([node.id]);
    let parent = node.parentId;
    while (parent) {
      if (visited.has(parent)) { graphError = '상위 조직이 서로 순환합니다. 상위 조직을 다시 선택해 주세요.'; break; }
      visited.add(parent); parent = finalById.get(parent)?.parentId;
    }
    if (node.id.startsWith('plan:') && finalNodes.some((other) => other.id !== node.id && !finalEnding.has(other.id) && other.parentId === node.parentId && other.name === node.name)) graphError = '같은 상위 조직 아래에 이름이 같은 조직이 있습니다. 신설 조직 이름을 구분해 주세요.';
  }
  if (next.workerChanges.some((row) => finalEnding.has(organizationId(row.organization)))) graphError = '종료할 조직을 이동 조직으로 지정한 구성원이 있습니다.';
  if (graphError || rows.some((row) => row.errors.length)) return { rows, error: graphError };
  return { rows, nextPlan: next };
}
